import React, { useState, useEffect } from 'react';
import './Quiz.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const maskEmail = (email) => {
  const [name, domain] = email.split('@');
  if (name.length <= 2) return name[0] + '***@' + domain;
  return name[0] + '****' + name[name.length - 1] + '@' + domain;
};

const Quiz = () => {
  // Initialize state with localStorage values
  const [currentQuestion, setCurrentQuestion] = useState(() => {
    const saved = localStorage.getItem('quizCurrentQuestion');
    const value = saved ? parseInt(saved) : 0;
    return value;
  });
  
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem('quizAnswers');
      const value = saved ? JSON.parse(saved) : {};
      return value;
    } catch (error) {
      console.error('Error parsing saved answers:', error);
      return {};
    }
  });
  
  const [showEmailOptIn, setShowEmailOptIn] = useState(() => {
    return localStorage.getItem('quizShowEmailOptIn') === 'true';
  });
  
  const [email, setEmail] = useState(() => {
    return localStorage.getItem('quizEmail') || '';
  });
  
  const [showResults, setShowResults] = useState(() => {
    return localStorage.getItem('quizShowResults') === 'true';
  });
  
  const [recommendation, setRecommendation] = useState(() => {
    try {
      const saved = localStorage.getItem('quizRecommendation');
      return saved ? JSON.parse(saved) : { name: '', image: '' };
    } catch (error) {
      return { name: '', image: '' };
    }
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('quizCurrentQuestion', currentQuestion.toString());
  }, [currentQuestion]);

  useEffect(() => {
    localStorage.setItem('quizAnswers', JSON.stringify(answers));
  }, [answers]);

  useEffect(() => {
    localStorage.setItem('quizShowEmailOptIn', showEmailOptIn.toString());
  }, [showEmailOptIn]);

  useEffect(() => {
    localStorage.setItem('quizShowResults', showResults.toString());
  }, [showResults]);

  useEffect(() => {
    localStorage.setItem('quizEmail', email);
  }, [email]);

  useEffect(() => {
    localStorage.setItem('quizRecommendation', JSON.stringify(recommendation));
  }, [recommendation]);

  const questions = [
    {
      id: 1,
      question: "Are you male or female?",
      options: [
        { text: "Male", value: "male" },
        { text: "Female", value: "female" }
      ]
    },

    {
      id: 3,
      question: "What's your biggest challenge right now?",
      options: [
        { text: "Getting started", value: "calisthenics" },
        { text: "Stiffness / flexibility", value: "mobility" },
        { text: "Feeling tired all the time", value: "energy" },
        { text: "Trouble sleeping", value: "sleep" },
        { text: "Always sore / plateauing", value: "recovery" }
      ]
    },
    {
      id: 4,
      question: "What's your #1 fitness goal?",
      options: [
        { text: "Build a consistent routine", value: "calisthenics" },
        { text: "Stay active & reduce stiffness", value: "mobility" },
        { text: "Boost daily energy", value: "energy" },
        { text: "Sleep better & feel rested", value: "sleep" },
        { text: "Train harder & recover faster", value: "recovery" }
      ]
    },
    {
      id: 5,
      question: "How much time do you realistically have per week for exercise?",
      options: [
        { text: "Less than 2 hours", value: "calisthenics" },
        { text: "2–4 hours", value: "mobility"},
        { text: "5+ hours", value: "recovery" }
      ]
    },
    {
      id: 6,
      question: "When do you feel you struggle most during the day?",
      options: [
        { text: "Morning or midday", value: "energy" },
        { text: "Evening wind-down", value: "sleep" }
      ]
    },
    {
      id: 7,
      question: "What motivates you most right now?",
      options: [
        { text: "Building a new habit", value: "calisthenics" },
        { text: "Moving freely", value: "mobility" },
        { text: "Having more energy", value: "energy" },
        { text: "Sleeping better", value: "sleep" },
        { text: "Avoiding burnout", value: "recovery" }
      ]
    }
  ];

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion]: value };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Quiz completed, calculate recommendation
      const recommendationData = calculateRecommendation(newAnswers);
      setRecommendation(recommendationData);
      
      // Save analytics for completed quiz
      const quizData = {
        answers: newAnswers,
        recommendation: recommendationData.name,
        gender: newAnswers[0] || 'unknown',
        timestamp: new Date().toISOString(),
        emailProvided: false,
        quizCompleted: true
      };
      saveAnalytics(quizData);
      
      setShowEmailOptIn(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateRecommendation = (answers) => {
    const scores = {
      calisthenics: 0,
      mobility: 0,
      energy: 0,
      sleep: 0,
      recovery: 0
    };

    // Weight coefficients for each category
    const weights = {
      calisthenics: 1.0,
      energy: 1.1,
      sleep: 1.11,
      mobility: 1.111,
      recovery: 1.1111
    };

    // Count scores for each category with weights
    Object.values(answers).forEach(answer => {
      if (answer === 'calisthenics') scores.calisthenics += weights.calisthenics;
      else if (answer === 'mobility') scores.mobility += weights.mobility;
      else if (answer === 'energy') scores.energy += weights.energy;
      else if (answer === 'sleep') scores.sleep += weights.sleep;
      else if (answer === 'recovery') scores.recovery += weights.recovery;
    });

    // Find the category with the highest score
    const maxScore = Math.max(...Object.values(scores));
    const topCategory = Object.keys(scores).find(cat => scores[cat] === maxScore);

    // Map to display names and image paths
    const recommendationData = {
      'calisthenics': {
        name: 'Calisthenics and Bodyweight Training',
        image: '/images/Calistenics.png'
      },
      'mobility': {
        name: 'Mobility and Flexibility',
        image: '/images/Mobility.png'
      },
      'energy': {
        name: 'Natural Energy',
        image: '/images/Energy.png'
      },
      'sleep': {
        name: 'Superior Sleep',
        image: '/images/Superior-Sleep.png'
      },
      'recovery': {
        name: 'Recovery Practices',
        image: '/images/Recovery.png'
      }
    };

    return recommendationData[topCategory] || recommendationData['calisthenics'];
  };

  const saveAnalytics = async (data) => {
    try {
      // Save analytics to server
      await fetch(`${API_URL}/api/save-analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error('Error saving analytics:', error);
      // Fallback: save to localStorage for later sending
      const existingData = JSON.parse(localStorage.getItem('quizAnalytics') || '[]');
      existingData.push(data);
      localStorage.setItem('quizAnalytics', JSON.stringify(existingData));
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (email) {
      const userData = {
        email: email,
        answers: answers,
        recommendation: recommendation.name,
        recommendationImage: recommendation.image,
        gender: answers[0] || 'unknown',
        timestamp: new Date().toISOString(),
        emailProvided: true,
        quizCompleted: true
      };

      // Save analytics
      await saveAnalytics(userData);

      try {
        // Option 1: Send via own server
        const response = await fetch(`${API_URL}/api/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData)
        });

        if (response.ok) {
          if (typeof window.fbq === 'function') { window.fbq('track', 'Lead'); }
          alert("Your plan has been sent! Check your inbox. If you don't see it, check your spam or junk folder.");
        } else {
          console.error('Failed to send email');
          alert("Something went wrong sending your email. Please try again.");
        }
        
        setShowEmailOptIn(false);
        setShowResults(true);
        
      } catch (error) {
        console.error('Error sending email:', error);
        
        alert("Your email has been saved. Please try again later.");
        setShowEmailOptIn(false);
        setShowResults(true);
      }
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowEmailOptIn(false);
    setShowResults(false);
    setEmail('');
    setRecommendation({ name: '', image: '' });
    
    // Clear localStorage
    localStorage.removeItem('quizCurrentQuestion');
    localStorage.removeItem('quizAnswers');
    localStorage.removeItem('quizShowEmailOptIn');
    localStorage.removeItem('quizShowResults');
    localStorage.removeItem('quizEmail');
    localStorage.removeItem('quizRecommendation');
  };

  if (showEmailOptIn) {
    return (
      <div className="quiz-container">
        <div className="logo-container">
          <img 
            src="/Wordmark_Orange_cropped.png" 
            alt="Fitneks Logo" 
            className="logo"
          />
        </div>
        <div className="email-optin">
          <h2>We've got your personalized recommendation ready!</h2>
          <p>Enter your email below and we'll send your free PDF instantly.</p>
          
          <div className="recommendation-image-standalone">
            <img 
              src={recommendation.image} 
              alt={recommendation.name}
              className="recommendation-img-standalone"
            />
            <h3>
              <span className="recommendation-label">Your Recommendation: </span>
              <span className="recommendation-name">{recommendation.name}</span>
            </h3>
          </div>
          
          <form onSubmit={handleEmailSubmit} className="email-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              className="email-input"
            />
            <button type="submit" className="submit-btn">
              Get My Plan
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="quiz-container">
        <div className="logo-container">
          <img 
            src="/Wordmark_Orange_cropped.png" 
            alt="Fitneks Logo" 
            className="logo"
          />
        </div>
        <div className="results">
          <h2>Thank You!</h2>
          <div className="success-message">
            <h3>✅ Email Sent Successfully!</h3>
            <p>Your personalized fitness plan has been sent to <strong>{maskEmail(email)}</strong></p>
            <p>Check your email for your detailed PDF with guidance on <strong>{recommendation.name}</strong></p>
          </div>
          
          <button onClick={resetQuiz} className="retake-btn">
            Take Another Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <div className="logo-container">
          <img 
            src="/Wordmark_Orange_cropped.png" 
            alt="Fitneks Logo" 
            className="logo"
          />
        </div>
        <h1>Find Your Perfect Fitness Plan</h1>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
        <p className="question-counter">
          Question {currentQuestion + 1} of {questions.length}
        </p>
      </div>

      <div className="question-container">
        <h2 className="question-text">
          {questions[currentQuestion].question}
        </h2>
        
        <div className="options-container">
          {questions[currentQuestion].options.map((option, index) => (
            <button
              key={index}
              className={`option-btn ${
                answers[currentQuestion] === option.value ? 'selected' : ''
              }`}
              onClick={() => handleAnswer(option.value)}
            >
              {option.text}
            </button>
          ))}
        </div>

        {currentQuestion > 0 && (
          <div className="navigation-buttons">
            <button
              className="back-btn"
              onClick={handleBack}
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;

